/**
 * Modal dialog with content required by:
 *  - 不動産情報ライブラリ API利用規約 第7条 (クレジット表示, verbatim)
 *  - 不動産情報ライブラリ API利用規約 第8条第1項 (運営者・責任範囲の明示)
 *  - 不動産情報ライブラリ 利用規約 第(1)項 (出典記載 + 編集・加工の表記)
 *  - 不動産情報ライブラリ 利用規約 第3条第4項 (参考情報用途である旨)
 *  - 国土数値情報 / 位置参照情報 / 国土地理院タイルの出典記載
 *
 * The Art. 7 credit text MUST be quoted verbatim. Do not paraphrase.
 *
 * Triggered from the header link "免責事項・クレジット". The MLIT terms
 * accept any location end users can reference, so a modal opened by an
 * always-visible header link satisfies the requirement.
 */

import { useEffect } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ComplianceModal({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      // z-[2000] beats Leaflet's default control z-index (1000) so the
      // modal isn't hidden behind the map.
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-ink/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="免責事項・クレジット"
    >
      <div
        className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-sheet shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 flex items-center justify-between border-b border-rule bg-sheet px-5 py-3">
          <h2 className="text-h2 font-medium text-ink">免責事項・クレジット</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="px-2 text-xl leading-none text-ink-3 hover:text-ink"
          >
            ×
          </button>
        </header>

        <div className="space-y-7 px-5 py-5 text-[13px] leading-jp text-ink-2">
          <section>
            <h3 className="section-title mb-2">本サービスについて</h3>
            <p>
              本サービス「マプカチ (MapKachi)」は個人の開発者により開発・運営されています。本サービスの提供する予測価格・近隣事例・最寄り駅情報等の正確性、完全性、有用性、目的適合性等について、運営者は一切の保証を行いません。
            </p>
            <p className="mt-2">
              本サービスの内容は、不動産取引における重要事項説明、契約、建築確認申請、その他法定の手続には使用できません。あくまで参考情報としてご利用ください。表示内容に基づいて利用者が行ったいかなる判断・行為に起因する損害についても、運営者は責任を負いません。
            </p>
            <p className="mt-2">
              本サービスに関するお問い合わせは{' '}
              <a
                href="mailto:realestate.maps.ai@gmail.com"
                className="text-primary-text underline underline-offset-2 hover:text-ink"
              >
                realestate.maps.ai@gmail.com
              </a>
              {' '}までご連絡ください（個人運営のため返信に時間をいただく場合があります）。
            </p>
          </section>

          <section>
            <h3 className="section-title mb-2">クレジット表示</h3>
            <p>
              このサービスは、国土交通省の不動産情報ライブラリのAPI機能を使用していますが、提供情報の最新性、正確性、完全性等が保証されたものではありません。
            </p>
          </section>

          <section>
            <h3 className="section-title mb-2">ガイド記事・エリアガイドについて</h3>
            <p>
              本サイトに掲載されているガイド記事・エリアガイド (/articles.html 等) は、不動産売却・税制・住宅ローン・業者選び等に関する一般的な情報提供を目的としています。執筆時点の制度・市場慣行に基づく概説であり、個別の取引・税務・法務判断にそのまま当てはまるとは限りません。
            </p>
            <p className="mt-2">
              具体的な不動産取引・税務・法務・住宅ローン等のご判断は、必ず<strong>宅地建物取引士・税理士・弁護士・各金融機関</strong>などの有資格者・専門家にご相談ください。記事中の試算・事例・数値には理解促進のための仮定値を含み、実際の数値や成約価格・節税額・所要期間等を保証するものではありません。本サイトの記述に基づいて行われた判断・行為について、運営者は一切の責任を負いかねます。
            </p>
            <p className="mt-2">
              また、本サイトは特定の不動産業者・税理士・金融機関・サービスを推奨または否定する意図を持つものではありません。
            </p>
          </section>

          <section>
            <h3 className="section-title mb-2">出典</h3>
            <ul className="ml-1 list-inside list-disc space-y-1">
              <li>
                不動産取引価格情報・成約価格情報：出典 国土交通省 不動産情報ライブラリ (
                <a
                  href="https://www.reinfolib.mlit.go.jp/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-text underline underline-offset-2 hover:text-ink"
                >
                  https://www.reinfolib.mlit.go.jp/
                </a>
                )
              </li>
              <li>
                行政区域データ (N03)・鉄道データ (N02)：出典 国土交通省 国土数値情報 (
                <a
                  href="https://nlftp.mlit.go.jp/ksj/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-text underline underline-offset-2 hover:text-ink"
                >
                  https://nlftp.mlit.go.jp/ksj/
                </a>
                )
              </li>
              <li>
                位置参照情報：出典 国土交通省 (
                <a
                  href="https://nlftp.mlit.go.jp/isj/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-text underline underline-offset-2 hover:text-ink"
                >
                  https://nlftp.mlit.go.jp/isj/
                </a>
                )
              </li>
              <li>
                地図タイル：地理院タイル (
                <a
                  href="https://maps.gsi.go.jp/development/ichiran.html"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-text underline underline-offset-2 hover:text-ink"
                >
                  国土地理院
                </a>
                )
              </li>
            </ul>
          </section>

          <section>
            <h3 className="section-title mb-2">編集・加工について</h3>
            <p>
              本サービスは、上記出典のデータをもとに、本サービス運営者が以下の編集・加工を行い、機械学習モデルの入力特徴量として利用しています。なお、本サービスや本サービスで使用しているデータの編集・加工に国土交通省は一切関与していません。
            </p>
            <ul className="ml-1 mt-2 list-inside list-disc space-y-1">
              <li>不動産取引価格情報を学習用に正規化（和暦→西暦変換、面積パース、外れ値除去 等）</li>
              <li>位置参照情報・鉄道データを地区センタロイド・最寄り駅距離として加工</li>
              <li>上記を回帰モデルで学習し、推論時に予測価格を生成</li>
            </ul>
          </section>

          <section className="text-[11px] text-ink-3">
            <p>
              本利用規約 (
              <a
                href="https://www.reinfolib.mlit.go.jp/help/term/"
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                不動産情報ライブラリ 利用規約・API利用規約
              </a>
              ) に基づくクレジット表示。
            </p>
          </section>
        </div>

        <footer className="sticky bottom-0 border-t border-rule bg-sheet px-5 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-primary px-6 py-2.5 text-[13px] font-bold tracking-wide text-sheet transition-colors hover:bg-primary-dark"
          >
            閉じる
          </button>
        </footer>
      </div>
    </div>
  );
}
